/**
 * Paleta Nava — fonte única de verdade de cores, COM TEMAS.
 *
 * Temas: `sphere` (PADRÃO desde 2026-10 — azul profundo + ciano, pacote
 * "Sphere"), `dark` (paleta Nava original) e `febraban` (azul institucional).
 *
 * COMO FUNCIONA O TEMA (sem tocar nos consumidores):
 *   `C` é um objeto MUTÁVEL cujas propriedades são valores HEX de 6 dígitos.
 *   `applyTheme(nome)` troca os valores DENTRO do mesmo objeto `C` (mesma
 *   referência), então todo `style={{ color: C.x }}` e todo `${C.x}22`
 *   (overlay com alpha de 2 dígitos hex) continuam válidos automaticamente.
 *   O ThemeProvider força um remount para os estilos inline relerem `C`.
 *
 *   IMPORTANTE: todos os valores de tema são HEX de 6 dígitos — não use
 *   rgba()/nomes aqui, senão o padrão `${C.x}<alpha>` quebraria.
 *
 * Chaves: família black (fundos), neutral (texto), cyber (ação/acento), cores de
 * apoio e aliases semânticos. Os aliases `bg/surface/surface2/border/dim/muted`
 * existem para as telas que tinham paleta local (mesmos nomes) passarem a seguir
 * o tema.
 */

// ─── Conjuntos de tema ────────────────────────────────────────────────────────

/**
 * Sphere — tokens de `sphere-design-handoff/src/tokens.json` mapeados para as
 * chaves de `C` (ver `src/ui/sphere/INTEGRACAO.md`):
 *   black/blackElev/blackElev2 ← --sp-bg / --sp-surface / --sp-surface-raised
 *   blackBorder ← --sp-border        neutral/Dim/Muted ← --sp-text/-secondary/-muted
 *   cyber ← --sp-accent (ação)       cyberDim ← --sp-border-bright
 *   digital ← --sp-cyan (energia)    quantum ← --sp-violet (pontual)
 *   hot ← --sp-danger   warn ← --sp-warning   ok ← --sp-success
 */
const SPHERE = {
  black: '#041225', blackElev: '#081B33', blackElev2: '#102845', blackBorder: '#20456C',
  neutral: '#EDF4FF', neutralDim: '#A8C0DF', neutralMuted: '#7D9BBC',
  cyber: '#159DFF', cyberDim: '#118FF0',
  quantum: '#785BFF', neon: '#9A85FF', hot: '#FF657B', hotLight: '#FFA3B1',
  digital: '#29D7FA', digitalLight: '#9BEBFF',
  text: '#EDF4FF', accent: '#159DFF', ok: '#36D9A0', bad: '#FF657B', warn: '#FFB32E',
  bg: '#041225', surface: '#081B33', surface2: '#102845', border: '#20456C',
  dim: '#A8C0DF', muted: '#7D9BBC',
  cyan: '#29D7FA', violet: '#785BFF', success: '#36D9A0', bgDeep: '#020B18',
  borderSoft: '#153653',
};

const DARK = {
  // Black family
  black: '#0C0C0C', blackElev: '#161616', blackElev2: '#1F1F1F', blackBorder: '#2A2A2A',
  // Neutral family
  neutral: '#F8F8F0', neutralDim: '#A8A8A0', neutralMuted: '#6B6B66',
  // Accent principal
  cyber: '#01E6E4', cyberDim: '#018E8C',
  // Cores de apoio
  quantum: '#5C00E0', neon: '#7400FE', hot: '#FF3300', hotLight: '#FFA29E',
  digital: '#FD00D5', digitalLight: '#FF9EE5',
  // Aliases semânticos (usados por algumas telas)
  text: '#F8F8F0', accent: '#01E6E4', ok: '#01E6E4', bad: '#FF3300', warn: '#FFB020',
  bg: '#0C0C0C', surface: '#161616', surface2: '#1F1F1F', border: '#2A2A2A',
  dim: '#A8A8A0', muted: '#6B6B66',
  cyan: '#01E6E4', violet: '#7400FE', success: '#01E6E4', bgDeep: '#0C0C0C',
  borderSoft: '#1F1F1F',
};

const FEBRABAN = {
  // Institucional Febraban — azul corporativo (tema escuro navy)
  black: '#0A1A2F', blackElev: '#102844', blackElev2: '#163454', blackBorder: '#274B6D',
  neutral: '#EAF2FB', neutralDim: '#A9C0D8', neutralMuted: '#6E88A2',
  cyber: '#1E88E5', cyberDim: '#1565C0',
  quantum: '#3949AB', neon: '#5C6BC0', hot: '#FF5252', hotLight: '#FF8A80',
  digital: '#26C6DA', digitalLight: '#80DEEA',
  text: '#EAF2FB', accent: '#1E88E5', ok: '#1E88E5', bad: '#FF5252', warn: '#FFB020',
  bg: '#0A1A2F', surface: '#102844', surface2: '#163454', border: '#274B6D',
  dim: '#A9C0D8', muted: '#6E88A2',
  cyan: '#26C6DA', violet: '#5C6BC0', success: '#26C6DA', bgDeep: '#071423',
  borderSoft: '#163454',
};

export const DEFAULT_THEME = 'sphere';

export const THEMES = Object.freeze({
  sphere: SPHERE, dark: DARK, febraban: FEBRABAN,
});

/** Ordem e rótulos exibidos no seletor de tema (o padrão vem primeiro). */
export const THEME_LIST = Object.freeze([
  { id: 'sphere',   label: 'Sphere',   hint: 'Padrão — azul profundo e ciano' },
  { id: 'dark',     label: 'Escuro',   hint: 'Paleta Nava original' },
  { id: 'febraban', label: 'Febraban', hint: 'Azul institucional' },
]);

/**
 * Chave do tema salvo. Versionada (`.v2`) na troca do padrão para o Sphere: a
 * escolha antiga (`nava.theme`) valia quando o padrão era o Escuro — o novo
 * visual aparece uma vez para todos e quem quiser volta ao Escuro em Configurações.
 */
export const THEME_STORAGE_KEY = 'nava.theme.v2';

function readStoredTheme() {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(THEME_STORAGE_KEY) : null;
  } catch {
    return null;
  }
}

/** Resolve o tema inicial (persistido e válido, senão o padrão Sphere). */
export function resolveInitialTheme() {
  const stored = readStoredTheme();
  return stored && THEMES[stored] ? stored : DEFAULT_THEME;
}

// ─── C: objeto mutável (mesma referência) trocado por applyTheme ─────────────

export const C = { ...SPHERE };

let currentTheme = DEFAULT_THEME;

/** Id do tema aplicado agora (sphere|dark|febraban). */
export function getCurrentTheme() {
  return currentTheme;
}

/**
 * applyTheme — troca os valores de `C` in-place para o tema informado.
 * Mantém a MESMA referência de objeto (importadores veem os novos valores).
 * @param {string} name id do tema (sphere|dark|febraban)
 * @returns {string} o id efetivamente aplicado
 */
export function applyTheme(name) {
  const id = THEMES[name] ? name : DEFAULT_THEME;
  Object.assign(C, THEMES[id]);
  currentTheme = id;
  return id;
}

/** Família tipográfica do tema: Inter local no Sphere; Host Grotesk nos demais. */
export function themeFontFamily(name = currentTheme) {
  return name === 'sphere'
    ? "InterVariable, Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
    : "'Host Grotesk', -apple-system, BlinkMacSystemFont, sans-serif";
}

/**
 * Variáveis CSS do shell/telas Sphere, derivadas do `C` ATUAL — o CSS do shell
 * (`src/ui/sphere/sphere.css`) só usa `var(--sp-*)`, então segue o tema escolhido.
 */
export function themeCssVars() {
  return {
    '--sp-bg': C.black,
    '--sp-bg-deep': C.bgDeep,
    '--sp-surface': C.blackElev,
    '--sp-surface-raised': C.blackElev2,
    '--sp-border': C.blackBorder,
    '--sp-border-soft': C.borderSoft,
    '--sp-border-bright': C.cyberDim,
    '--sp-text': C.neutral,
    '--sp-text-secondary': C.neutralDim,
    '--sp-text-muted': C.neutralMuted,
    '--sp-accent': C.cyber,
    '--sp-cyan': C.cyan,
    '--sp-violet': C.violet,
    '--sp-success': C.success,
    '--sp-warning': C.warn,
    '--sp-danger': C.hot,
    '--sp-font': themeFontFamily(),
  };
}

// Aplica o tema persistido JÁ no carregamento do módulo — antes de qualquer
// consumidor importar `C` —, garantindo cores corretas na primeira pintura
// (inclusive constantes de estilo avaliadas em nível de módulo).
applyTheme(resolveInitialTheme());
